import React, { useEffect, useState, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { AccountTree } from '../components/AccountTree';
import { AccountNode, getBalance, getDeclaredAccounts, createAccount, updateAccount, deleteAccount } from '../services/apiService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export default function AccountsPage() {
  const { canWrite } = useAuth();
  const [accounts, setAccounts] = useState<AccountNode[]>([]);
  const [declaredAccounts, setDeclaredAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formParent, setFormParent] = useState('');
  const [formCommodity, setFormCommodity] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [balanceRes, declaredRes] = await Promise.all([
        getBalance(),
        getDeclaredAccounts(),
      ]);
      setAccounts(balanceRes.accounts);
      setDeclaredAccounts(declaredRes.accounts);
    } catch (err) {
      console.error('Failed to load accounts', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditingAccount(null);
    setFormName('');
    setFormParent('');
    setFormCommodity('');
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (fullPath: string) => {
    setEditingAccount(fullPath);
    const parts = fullPath.split(':');
    setFormName(parts[parts.length - 1]);
    setFormParent(parts.length > 1 ? parts.slice(0, -1).join(':') : '');
    setFormCommodity('');
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    try {
      const fullName = formParent ? `${formParent}:${formName}` : formName;

      if (editingAccount) {
        await updateAccount(editingAccount, {
          new_name: fullName !== editingAccount ? fullName : undefined,
          commodity: formCommodity || undefined,
        });
      } else {
        await createAccount({
          name: fullName,
          commodity: formCommodity || undefined,
        });
      }

      setShowForm(false);
      setLoading(true);
      await loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || err.message || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (fullPath: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir a conta "${fullPath}"?`)) {
      return;
    }
    try {
      await deleteAccount(fullPath);
      setLoading(true);
      await loadData();
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'Erro ao excluir';
      alert(detail);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Contas Contábeis</h1>
        {canWrite && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Nova Conta
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              {editingAccount ? 'Editar Conta' : 'Nova Conta'}
            </h2>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-4">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conta Pai</label>
                <select
                  value={formParent}
                  onChange={(e) => setFormParent(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">(Raiz)</option>
                  {declaredAccounts.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Nome da conta"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commodity (opcional)</label>
                <select
                  value={formCommodity}
                  onChange={(e) => setFormCommodity(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Nenhum</option>
                  <option value="BRL">BRL</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Salvando...' : editingAccount ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border rounded-lg shadow-sm">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">Plano de Contas</h2>
        </div>
        <div className="p-4">
          <AccountTree
            accounts={accounts}
            canWrite={canWrite}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>
    </div>
  );
}
